/**
 * Slime Wars messages (docs/14-SLIME-WARS/TECH.md §8–9). Clients send INTENT only: lobby
 * requests, held keys and one-shot `WarsCommand`s. Everything else is server → client.
 */
import type {
  BotDifficulty,
  ClassId,
  SeatTeam,
  TauntId,
  HeldKeys,
  WarsCommand,
  WarsEffectEvent,
  WarsEvent,
  WarsInventory,
  WarsPlacement,
  WarsSettings,
} from '@slimez/wars-core';
import { WARS_TICK_RATE_HZ } from '@slimez/wars-core';
import type { WarsAchievementId } from './achievements';

export const WARS_MESSAGES = Object.freeze({
  // client → server
  /** `WarsLobbyRequest`; answered with `wars.reply`. */
  lobby: 'wars.lobby',
  /** `WarsHeldPayload`, sent on change. */
  held: 'wars.held',
  /** `WarsCommand`. Not answered (the state / events show the outcome). */
  cmd: 'wars.cmd',
  /** `{}` — the client's terrain copy has a version gap; the server re-sends `wars.terrain`. */
  terrainRequest: 'wars.terrain.request',

  // both directions (W029c, ADR-042; `emotes.ts`)
  /** Client: `WarsEmoteRequest`, not answered. Server: `WarsEmotePayload` to every client. */
  emote: 'wars.emote',
  /**
   * W064 (ADR-061; `chat.ts`), LOBBY of an online room only. Client: `WarsChatRequest`, not
   * answered. Server: `WarsChatPayload` to every client, the sender included.
   */
  chat: 'wars.chat',

  // server → client
  /** `WarsSelfPayload`, to that client only, on join / reconnect / host change. */
  self: 'wars.self',
  /** `WarsTerrainPayload`, on match start and on every (re)join during a match. */
  terrain: 'wars.terrain',
  /** `WarsEventsPayload`, one batch per tick that has events (or status-effect events). */
  events: 'wars.events',
  /** `WarsInventoryPayload`, owner-only, whenever it changes and on (re)join. */
  inventory: 'wars.inventory',
  /** `WarsResultsPayload`, on match end and on (re)join during RESULTS. */
  results: 'wars.results',
  /** `WarsReply`, to the requester only. */
  reply: 'wars.reply',
  /**
   * W058 (`stats.ts`): `WarsRecordsPayload`, to the improved player only, after `wars.results`
   * (and again on a reconnect in RESULTS); never when nothing improved on a record above 0.
   */
  records: 'wars.records',
} as const);

export type WarsLobbyRequest =
  | { readonly action: 'class'; readonly classId: ClassId }
  /** The own equipped taunt (W030, ADR-043; LOBBY). */
  | { readonly action: 'taunt'; readonly taunt: TauntId }
  | { readonly action: 'ready'; readonly ready: boolean }
  /** Host only; `patch` is a partial `WarsSettings` (unknown keys / values ignored). */
  | { readonly action: 'settings'; readonly patch: Partial<WarsSettings> }
  /** Host only. */
  | { readonly action: 'addBot'; readonly difficulty: BotDifficulty }
  /** Host only; `slot` must hold a bot. */
  | { readonly action: 'removeBot'; readonly slot: number }
  /** Host only (RULES §11 start gate). */
  | { readonly action: 'start' }
  /** Host only, RESULTS → LOBBY. */
  | { readonly action: 'playAgain' }
  /** W044 (ADR-051, TEAMS.md §3.2): the own seat's team (LOBBY): 0 Solo, 1 … 4 = A–D. */
  | { readonly action: 'team'; readonly team: SeatTeam }
  /** W044: host only (LOBBY): any seat's team, a bot's or the host's own included. */
  | { readonly action: 'moveTeam'; readonly slot: number; readonly team: SeatTeam };

export type WarsLobbyAction = WarsLobbyRequest['action'];

export const WARS_LOBBY_ACTIONS = [
  'class',
  'taunt',
  'ready',
  'settings',
  'addBot',
  'removeBot',
  'start',
  'playAgain',
  'team',
  'moveTeam',
] as const satisfies readonly WarsLobbyAction[];

export type WarsHeldPayload = HeldKeys;

export type WarsCommandPayload = WarsCommand;

export interface WarsSelfPayload {
  readonly slot: number;
  readonly host: boolean;
  /**
   * W060 (P3-BOARDS §2): set by the ranked Daily Challenge room only — the server's UTC date of
   * the attempt (`YYYY-MM-DD`), so the client shows the ranked results (also after a reload).
   */
  readonly daily?: string;
}

export interface WarsTerrainPayload {
  /** Terrain version the bytes are at. */
  readonly version: number;
  readonly width: number;
  readonly height: number;
  /** `encodeTerrain` output (wars-core). */
  readonly bytes: Uint8Array;
}

export interface WarsEventsPayload {
  readonly serial: number;
  readonly events: readonly WarsEvent[];
  /**
   * The tick's generic status-effect events (ADR-038, W021b: wars-core's `match.effectEvents()`,
   * `effectApplied` / `effectEnded`), in creation order; absent when there are none. A separate
   * list so `events` stays exactly the `WarsEvent` contract an older client checks type by type.
   */
  readonly effects?: readonly WarsEffectEvent[];
}

/**
 * Everything a client plays from a match, in tick order (W021b): the `WarsEvent` stream and the
 * status-effect events beside it. Both sources (the room's `wars.events`, offline Practice) feed
 * the presentation one ordered list of these.
 */
export type WarsMatchEvent = WarsEvent | WarsEffectEvent;

/** The status-effect events among `WarsMatchEvent`s. */
export function isWarsEffectEvent(event: { readonly type: string }): event is WarsEffectEvent {
  return event.type === 'effectApplied' || event.type === 'effectEnded';
}

export interface WarsInventoryPayload {
  readonly serial: number;
  readonly items: WarsInventory;
}

export interface WarsResultPlayer extends WarsPlacement {
  readonly name: string;
  readonly classId: ClassId;
  readonly bot: boolean;
  /**
   * W069 (P4a, owner D2): the title this human seat wears — a catalogue id the server read from the
   * seat's own profile (never a client's claim); absent for none and for bots.
   */
  readonly title?: WarsAchievementId;
}

export interface WarsResultsPayload {
  readonly serial: number;
  /** null = draw (a team match: the lowest living slot of the winning side, W044). */
  readonly winner: number | null;
  /**
   * W044 (TEAMS.md §5.1): a team match only (absent in a free-for-all) — the winning side's team,
   * 0 for a Solo seat, null for a draw. Each player then carries `team`, `teamDamage` and
   * `teamKills` (`WarsPlacement`).
   */
  readonly winnerTeam?: SeatTeam | null;
  readonly players: readonly WarsResultPlayer[];
}

export const WARS_REJECT_REASONS = [
  'WRONG_PHASE',
  'NOT_HOST',
  'NOT_SEATED',
  'NOT_ENOUGH_PLAYERS',
  'NOT_ALL_READY',
  'ROOM_FULL',
  'NOT_A_BOT',
  'RATE_LIMITED',
  'MALFORMED',
  'START_FAILED',
  /** W044 (TEAMS.md §3.4): a team match needs at least two sides (everyone is on one team). */
  'ONE_TEAM',
] as const;
export type WarsRejectReason = (typeof WARS_REJECT_REASONS)[number];

export interface WarsReply {
  readonly ok: boolean;
  readonly action?: WarsLobbyAction;
  readonly reason?: WarsRejectReason;
}

/** Wire limits (TUNING). */
export const WARS_LIMITS = Object.freeze({
  /** Players (humans + bots) per room. */
  maxPlayers: 8,
  minPlayers: 2,
  /** Room code: length and alphabet (TECH §8; the Preserve's ids are 9 characters). */
  roomCodeLength: 7,
  roomCodeAlphabet: 'ABCDEFGHJKMNPQRSTUVWXYZ23456789',
  /** Per-client minimum interval between two lobby requests of the same action. */
  lobbyRequestMinIntervalMs: 150,
  /** Per-client caps per second. */
  heldPerSecond: 40,
  commandsPerSecond: 30,
  /** Malformed messages tolerated per window before the client is dropped (T013-FIX pattern). */
  malformedDropThreshold: 6,
  malformedWindowMs: 10_000,
  /** Reconnect windows (RULES §3; TECH §9). */
  matchReconnectSeconds: 60,
  lobbyReconnectGraceSeconds: 15,
  /**
   * Idle-room limits (TECH §9; W008-R o5): a room closes itself (`WARS_CLOSE_CODES.idle`) after
   * this long in LOBBY without a match start, or this long in RESULTS without a play again, so
   * idle rooms cannot hold the server's room cap.
   */
  lobbyIdleMinutes: 20,
  resultsIdleMinutes: 10,
} as const);

/**
 * W047 (ADR-053): the `wars` room sends one state patch at the end of every fixed step (30 Hz, the
 * match's tick rate; no patch timer), so each patch carries exactly the tick it was taken after.
 * The interval between two patches (ms). The Preserve keeps `STATE_PATCH_INTERVAL_MS` (20 Hz).
 */
export const WARS_PATCH_INTERVAL_MS = 1000 / WARS_TICK_RATE_HZ;

/** Close codes of the wars room besides Colyseus' own (TECH §9). */
export const WARS_CLOSE_CODES = Object.freeze({
  /**
   * The room closed itself for inactivity (`lobbyIdleMinutes` in LOBBY / `resultsIdleMinutes` in
   * RESULTS): the client shows "lobby closed for inactivity". Final: no reconnect.
   */
  idle: 4100,
} as const);

/** Whether `code` has the Slime Wars room-code shape (routing only; never authorization). */
export function isWarsRoomCode(code: string): boolean {
  if (code.length !== WARS_LIMITS.roomCodeLength) return false;
  for (const ch of code) if (!WARS_LIMITS.roomCodeAlphabet.includes(ch)) return false;
  return true;
}
